import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'hooks/useTranslation';
import { useCountriesScroll } from '../../hooks/useCountriesScroll';
import { TRANSLATION_KEYS, COUNTRY_TRANSLATION_KEYS } from '../../constants';
import CountryCard from './CountryCard';
import { LETTER_FILTERS, VIEW_FILTERS } from './filters';
import {
  setWCCountries,
  setWCLetterFilter,
  setWCViewFilter,
  toggleWCCountryFavorite,
} from '../../reducer';
import {
  addCompetitionFavorite,
  fetchCompetitionCountries,
  removeCompetitionFavorite,
} from '../../services';
import './WCCountries.scss';
import { useMundialMetrics } from '../../../hooks/useMundialMetrics';
import { INTERACTION_TYPES } from '../../../hooks/useMundialMetrics/constants';
import { useMundialScrollTracker } from '../../../hooks/useMundialScrollTracker';

const VIEW_FILTER_PARAM = 'viewFilter';
const LETTER_FILTER_PARAM = 'letterFilter';
const DEFAULT_VIEW_FILTER = 'all';
const DEFAULT_LETTER_FILTER = 'all';

const WCCountries = () => {
  const dispatch = useDispatch();
  const { translationMapper } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const tabpanel = document.querySelector(
      '[data-role="mundial2026-tabpanel"]',
    );
    if (tabpanel) tabpanel.setAttribute('data-countries-active', '');
    return () => {
      if (tabpanel) tabpanel.removeAttribute('data-countries-active');
    };
  }, []);
  const assets = useSelector(state => state.asset);
  const {
    countries = [],
    loaded = false,
    viewFilter: storedViewFilter = DEFAULT_VIEW_FILTER,
    letterFilter: storedLetterFilter = DEFAULT_LETTER_FILTER,
  } = useSelector(state => state.wcCountries || {});
  const viewFilterParam = searchParams.get(VIEW_FILTER_PARAM);
  const viewFilter = VIEW_FILTERS.some(filter => filter.id === viewFilterParam)
    ? viewFilterParam
    : DEFAULT_VIEW_FILTER;
  const letterFilterParam = searchParams.get(LETTER_FILTER_PARAM);
  const letterFilter = LETTER_FILTERS.some(
    filter => filter.id === letterFilterParam,
  )
    ? letterFilterParam
    : DEFAULT_LETTER_FILTER;

  const { countriesRef, viewRef, gridRef, recompute, resetScroll } = useCountriesScroll();
  const [subtabHidden, setSubtabHidden] = useState(false);

  useEffect(() => {
    const container = countriesRef.current?.closest('.content_div');
    const mundial = countriesRef.current?.closest('[data-role="mundial2026"]');
    if (!container) return;
    const handleScroll = () => {
      if (window.innerWidth >= 1024) {
        setSubtabHidden(false);
        mundial?.removeAttribute('data-standings-scrolled');
        return;
      }
      const scrolled = container.scrollTop > 0;
      setSubtabHidden(scrolled);
      if (mundial) {
        if (scrolled) mundial.setAttribute('data-standings-scrolled', '');
        else mundial.removeAttribute('data-standings-scrolled');
      }
    };
    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      container.removeEventListener('scroll', handleScroll);
      if (mundial) mundial.removeAttribute('data-standings-scrolled');
    };
  }, []);

  useEffect(() => {
    recompute();
  }, [subtabHidden, recompute]);

  useEffect(() => {
    resetScroll();
    recompute();
  }, [viewFilter, resetScroll, recompute]);

  const { trackSelectContent } = useMundialMetrics();

  useMundialScrollTracker({
    pageTitleSegments: ['paises'],
    contentSubsection: 'paises',
    moduloName: '',
  });

  const translatedTexts = useMemo(() => translationMapper(TRANSLATION_KEYS), [
    translationMapper,
  ]);

  useEffect(
    () => {
      let cancelled = false;

      if (loaded) {
        return undefined;
      }

      const loadTeams = async () => {
        try {
          const nextCountries = await fetchCompetitionCountries();

          if (!cancelled && nextCountries.length > 0) {
            dispatch(setWCCountries(nextCountries));
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Failed to load Mundial 2026 teams', error);
        }
      };

      loadTeams();

      return () => {
        cancelled = true;
      };
    },
    [dispatch, loaded],
  );

  useEffect(
    () => {
      if (storedViewFilter === viewFilter) return;
      dispatch(setWCViewFilter(viewFilter));
    },
    [dispatch, storedViewFilter, viewFilter],
  );

  useEffect(
    () => {
      if (storedLetterFilter === letterFilter) return;
      dispatch(setWCLetterFilter(letterFilter));
    },
    [dispatch, storedLetterFilter, letterFilter],
  );

  const countryNames = useMemo(
    () => translationMapper(COUNTRY_TRANSLATION_KEYS),
    [translationMapper],
  );

  const handleFavoriteToggle = async countryId => {
    const country = countries.find(item => item.id === countryId);

    if (!country) return;

    try {
      if (country.favorite) {
        await removeCompetitionFavorite(countryId);
      } else {
        await addCompetitionFavorite(countryId);
      }

      dispatch(toggleWCCountryFavorite(countryId));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to update Mundial 2026 favorite', error);
    }
  };

  const handleCountryClick = (countryCode, countryName) => {
    trackSelectContent({
      pageTitleSegments: ['paises', countryName],
      pageTitle: countryName,
      contentSubsection: 'paises',
      moduloName: countryName,
      interactionType: INTERACTION_TYPES.CARD_SELECT,
    });
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        next.set('countryId', countryCode);
        return next;
      },
      { replace: true },
    );
  };

  const filteredCountries = useMemo(
    () => {
      const filtered = (countries || []).filter(country => {
        if (viewFilter === 'favorites') {
          return country.favorite;
        }
        if (letterFilter !== 'all' && country.letter !== letterFilter)
          return false;

        return true;
      });

      if (viewFilter === 'favorites') {
        return [...filtered].sort((a, b) => {
          const nameA = (a.name || countryNames[a.nameKey] || '').toLowerCase();
          const nameB = (b.name || countryNames[b.nameKey] || '').toLowerCase();
          return nameA.localeCompare(nameB);
        });
      }

      return filtered;
    },
    [letterFilter, viewFilter, countries, countryNames],
  );

  const panelClass = 'wc-countries__panel';

  return (
    <div ref={countriesRef} className="wc-countries" data-subtab-hidden={subtabHidden ? '' : undefined}>
      <section className="wc-countries__content">
        <div className="wc-countries__view-filters" role="tablist">
          {VIEW_FILTERS.map(filter => (
            <button
              key={filter.id}
              type="button"
              role="tab"
              aria-selected={viewFilter === filter.id}
              className={`wc-countries__view-filter${
                viewFilter === filter.id
                  ? ' wc-countries__view-filter--active'
                  : ''
              }`}
              onClick={() => {
                const subTabLabel = translatedTexts[filter.labelKey] || filter.id;
                trackSelectContent({
                  pageTitleSegments: ['paises', subTabLabel],
                  pageTitle: subTabLabel,
                  contentSubsection: 'paises',
                  moduloName: '',
                  interactionType: INTERACTION_TYPES.MAIN_TABS,
                });
                setSearchParams(
                  prev => {
                    const next = new URLSearchParams(prev);
                    if (filter.id === DEFAULT_VIEW_FILTER) {
                      next.delete(VIEW_FILTER_PARAM);
                    } else {
                      next.set(VIEW_FILTER_PARAM, filter.id);
                    }
                    return next;
                  },
                  { replace: true },
                );
                dispatch(setWCViewFilter(filter.id));
                setSubtabHidden(false);
              }}
            >
              {translatedTexts[filter.labelKey]}
            </button>
          ))}
        </div>

        <section ref={viewRef} className={panelClass}>
          {viewFilter !== 'favorites' && (
            <div className="wc-countries__letters" role="tablist">
              {LETTER_FILTERS.map(letter => (
                <button
                  key={letter.id}
                  type="button"
                  role="tab"
                  aria-selected={letterFilter === letter.id}
                  className={`wc-countries__letter${
                    letterFilter === letter.id
                      ? ' wc-countries__letter--active'
                      : ''
                  }`}
                  onClick={() => {
                    setSearchParams(
                      prev => {
                        const next = new URLSearchParams(prev);
                        if (letter.id === DEFAULT_LETTER_FILTER) {
                          next.delete(LETTER_FILTER_PARAM);
                        } else {
                          next.set(LETTER_FILTER_PARAM, letter.id);
                        }
                        return next;
                      },
                      { replace: true },
                    );
                    dispatch(setWCLetterFilter(letter.id));
                  }}
                >
                  {letter.labelKey
                    ? translatedTexts[letter.labelKey]
                    : letter.id}
                </button>
              ))}
            </div>
          )}

          <div ref={gridRef} className={`wc-countries__grid${viewFilter === 'favorites' && filteredCountries.length > 0 ? ' wc-countries__grid--favorites' : ''}${filteredCountries.length === 1 ? ' wc-countries__grid--single' : ''}`}>
            {viewFilter === 'favorites' && filteredCountries.length === 0 ? (
              <div className="wc-countries__empty">
                <p className="wc-countries__empty-title">
                  {translatedTexts.EMPTY_TITLE}
                </p>
                <p className="wc-countries__empty-text">
                  {translatedTexts.EMPTY_TEXT_1}{' '}
                  <span className="wc-countries__empty-star">☆</span>{' '}
                  {translatedTexts.EMPTY_TEXT_2}
                </p>
              </div>
            ) : (
              filteredCountries.map(country => (
                <CountryCard
                  key={country.id}
                  country={country}
                  onFavorite={handleFavoriteToggle}
                  onClick={() => handleCountryClick(country.code, country.name || countryNames[country.nameKey])}
                  translatedTexts={translatedTexts}
                  countryName={country.name || countryNames[country.nameKey]}
                  assets={assets}
                />
              ))
            )}
          </div>
        </section>
      </section>
    </div>
  );
};

export default WCCountries;
